import axios from 'axios';
import { signInUrl } from '../utils/authRedirect';

// Ensure HTTPS is used in production (Cloud Run)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // Always use HTTPS for Cloud Run domains (even if env var has HTTP)
    if (envUrl.includes('run.app')) {
      // Replace http:// with https://
      let url = envUrl.replace(/^http:\/\//, 'https://');
      // Ensure it starts with https://
      if (!url.startsWith('https://')) {
        url = 'https://' + url.replace(/^https?:\/\//, '');
      }
      return url;
    }
    return envUrl;
  }
  // Default to localhost for development
  return 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const learningService = {
  workspace: async () => (await api.get('/learning')).data,
  createArea: async values => (await api.post('/learning/areas', values)).data,
  updateArea: async (id, values) => (await api.patch(`/learning/areas/${id}`, values)).data,
  reorderAreas: async ids => (await api.put('/learning/area-order', {ids})).data,
  deleteArea: async id => (await api.delete(`/learning/areas/${id}`)).data,
  createItem: async values => (await api.post('/learning/items', values)).data,
  updateItem: async (id, values) => (await api.patch(`/learning/items/${id}`, values)).data,
  deleteItem: async id => (await api.delete(`/learning/items/${id}`)).data,
  saveNote: async text => (await api.post('/learning/file', {text})).data,
  fileSavedNote: async id => (await api.post(`/learning/notes/${id}/file`)).data,
  draftShareText: async (format, item_ids) => (await api.post('/learning/draft', {format, item_ids})).data,
};

export const challengeService = {
  list: async () => (await api.get('/challenges')).data,
  detail: async id => (await api.get(`/challenges/${id}`)).data,
  create: async values => (await api.post('/challenges', values)).data,
  update: async (id, values) => (await api.patch(`/challenges/${id}`, values)).data,
  adjust: async (id, delta, event_date) => (await api.post(`/challenges/${id}/adjust`, {delta, event_date})).data,
  setCount: async (id, count, note) => (await api.post(`/challenges/${id}/count`, {count, note})).data,
  remove: async id => (await api.delete(`/challenges/${id}`)).data,
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    config.timelyzerSessionToken = token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    if (response.config?.timelyzerSessionToken !== localStorage.getItem('access_token')) {
      return Promise.reject(new axios.CanceledError('Your session changed.'));
    }
    if (['post', 'put', 'patch', 'delete'].includes(response.config?.method)) window.dispatchEvent(new Event('timelyzer:records-changed'));
    return response;
  },
  (error) => {
    const currentToken = localStorage.getItem('access_token');
    if (error.config && error.config.timelyzerSessionToken !== currentToken) {
      return Promise.reject(new axios.CanceledError('Your session changed.'));
    }
    const sessionRejected = error.response?.status === 401 ||
      (error.response?.status === 403 && error.response?.data?.detail === 'Inactive user');
    if (sessionRejected && currentToken && error.config?.timelyzerSessionToken === currentToken) {
      localStorage.removeItem('access_token');
      const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password'];
      if (!publicPaths.includes(window.location.pathname)) {
        window.location.href = signInUrl(window.location.pathname + window.location.search + window.location.hash);
      }
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await api.post('/auth/token', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    });
    
    // AuthProvider stores the token only if this is still the current login.
    return response.data;
  },

  requestPasswordReset: async (identifier) => {
    const response = await api.post('/auth/password-reset/request', { identifier });
    return response.data;
  },

  confirmPasswordReset: async (token, newPassword) => {
    const response = await api.post('/auth/password-reset/confirm', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('access_token');
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me', { timeout: 90000 });
    return response.data;
  },

  updateUser: async (userData) => {
    const response = await api.put('/auth/me', userData);
    return response.data;
  },
};

// Event services
export const eventService = {
  getEvents: async (params = {}) => {
    const response = await api.get('/events/', { params });
    return response.data;
  },

  getEvent: async (eventId) => {
    const response = await api.get(`/events/${eventId}`);
    return response.data;
  },

  createEvent: async (eventData) => {
    const response = await api.post('/events/', eventData);
    return response.data;
  },

  updateEvent: async (eventId, eventData) => {
    const response = await api.put(`/events/${eventId}`, eventData);
    return response.data;
  },

  deleteEvent: async (eventId) => {
    const response = await api.delete(`/events/${eventId}`);
    return response.data;
  },

  enhanceEvent: async (eventId) => {
    const response = await api.post(`/events/${eventId}/enhance`);
    return response.data;
  },

  getEventRecommendations: async (eventId) => {
    const response = await api.get(`/events/${eventId}/recommendations`);
    return response.data;
  },

  acceptRecommendation: async (eventId, recommendationId) => {
    const response = await api.put(`/events/${eventId}/recommendations/${recommendationId}/accept`);
    return response.data;
  },

  dismissRecommendation: async (eventId, recommendationId, feedback = null) => {
    const response = await api.put(`/events/${eventId}/recommendations/${recommendationId}/dismiss`, { feedback });
    return response.data;
  },
};

// Note services
export const noteService = {
  getNotes: async (params = {}) => {
    const response = await api.get('/notes/', { params });
    return response.data;
  },

  getNote: async (noteId) => {
    const response = await api.get(`/notes/${noteId}`);
    return response.data;
  },

  createNote: async (noteData) => {
    const response = await api.post('/notes/', noteData);
    return response.data;
  },

  updateNote: async (noteId, noteData) => {
    const response = await api.put(`/notes/${noteId}`, noteData);
    return response.data;
  },

  deleteNote: async (noteId) => {
    const response = await api.delete(`/notes/${noteId}`);
    return response.data;
  },

  archiveNote: async (noteId) => {
    const response = await api.post(`/notes/${noteId}/archive`);
    return response.data;
  },

  unarchiveNote: async (noteId) => {
    const response = await api.post(`/notes/${noteId}/unarchive`);
    return response.data;
  },

  pinNote: async (noteId) => {
    const response = await api.post(`/notes/${noteId}/pin`);
    return response.data;
  },

  unpinNote: async (noteId) => {
    const response = await api.post(`/notes/${noteId}/unpin`);
    return response.data;
  },

  getNoteEvents: async (noteId) => {
    const response = await api.get(`/notes/${noteId}/events`);
    return response.data;
  },

  reprocessNote: async (noteId) => {
    const response = await api.post(`/notes/${noteId}/reprocess`);
    return response.data;
  },
};

// AI Chat services
export const chatService = {
  sendMessage: async (message, context = null) => {
    const response = await api.post('/chat/', { message, context });
    return response.data;
  },

  getChatHistory: async (params = {}) => {
    const response = await api.get('/chat/history', { params });
    return response.data;
  },

  getInteraction: async (interactionId) => {
    const response = await api.get(`/chat/history/${interactionId}`);
    return response.data;
  },

  deleteInteraction: async (interactionId) => {
    const response = await api.delete(`/chat/history/${interactionId}`);
    return response.data;
  },

  clearHistory: async () => {
    const response = await api.post('/chat/clear-history');
    return response.data;
  },

  getSuggestions: async () => {
    const response = await api.get('/chat/suggestions');
    return response.data;
  },
};

// Dashboard services
export const dashboardService = {
  getDashboard: async (daysAhead = 7) => {
    const response = await api.get('/dashboard/', { params: { days_ahead: daysAhead } });
    return response.data;
  },

  getInsights: async () => {
    const response = await api.get('/dashboard/insights');
    return response.data;
  },

  getCalendarView: async (startDate, endDate) => {
    const response = await api.get('/dashboard/calendar-view', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data;
  },
};

// Context graph, Today, Inbox, and universal capture
export const meetingService = {
  briefing: async (id) => (await api.get(`/meetings/${id}/briefing`)).data,
  transcriptions: async (id, params) => (await api.get(`/meetings/${id}/transcriptions`, { params })).data,
  transcribe: async (id, file, requestId, provider = 'openai') => (await api.post(`/meetings/${id}/transcriptions`, file, {
    params: {audio_format:file.name.split('.').pop().toLowerCase(), request_id:requestId, consent_provider:provider},
    headers: {'Content-Type':'application/octet-stream'}, timeout:180000,
  })).data,
  cancelTranscription: async (id, attemptId) => (await api.post(`/meetings/${id}/transcriptions/${attemptId}/cancel`)).data,
  get: async (id, params) => (await api.get(`/meetings/${id}`, { params })).data,
  configure: async (id, data) => (await api.put(`/meetings/${id}`, data)).data,
  capture: async (id, data) => (await api.post(`/meetings/${id}/notes`, data)).data,
  sourceOptions: async (id, params) => (await api.get(`/meetings/${id}/source-options`, { params })).data,
  linkSource: async (id, data) => (await api.post(`/meetings/${id}/sources`, data)).data,
  detachSource: async (id, data) => (await api.post(`/meetings/${id}/sources/detach`, data)).data,
  analyse: async (id, data) => (await api.post(`/meetings/${id}/analyse`, data)).data,
  createAction: async (id, data) => (await api.post(`/meetings/${id}/actions`, data)).data,
  review: async (id, data) => (await api.post(`/meetings/${id}/review`, data)).data,
  followup: async (id) => (await api.get(`/meetings/${id}/follow-up`)).data,
  draftFollowup: async (id, data) => (await api.post(`/meetings/${id}/follow-up`, data)).data,
  followupPolicy: async (id, data) => (await api.put(`/meetings/${id}/follow-up-policy`, data)).data,
};

export const contextService = {
  getToday: async () => {
    const response = await api.get('/today');
    return response.data;
  },

  getContexts: async (params = {}) => {
    const response = await api.get('/contexts', { params });
    return response.data;
  },

  getContext: async (contextId) => {
    const response = await api.get(`/contexts/${contextId}`);
    return response.data;
  },

  createContext: async (contextData) => {
    const response = await api.post('/contexts', contextData);
    return response.data;
  },

  updateContext: async (contextId, contextData) => {
    const response = await api.patch(`/contexts/${contextId}`, contextData);
    return response.data;
  },

  addContextLink: async (contextId, linkData) => {
    const response = await api.post(`/contexts/${contextId}/links`, linkData);
    return response.data;
  },

  removeContextLink: async (contextId, linkId) => {
    await api.delete(`/contexts/${contextId}/links/${linkId}`);
  },

  capture: async (captureData) => {
    const response = await api.post('/capture', captureData);
    return response.data;
  },

  getInbox: async (params = {}) => {
    const response = await api.get('/inbox', { params });
    return response.data;
  },

  resolveInboxItem: async (itemId, resolution) => {
    const response = await api.post(`/inbox/${itemId}/resolve`, resolution);
    return response.data;
  },
};

// Enhanced Google Integrations services
export const googleIntegrationsService = {
  taskMonitoring: async () => (await api.get('/google/tasks/monitoring')).data,
  saveTaskMonitoring: async (payload) => (await api.put('/google/tasks/monitoring', payload)).data,
  taskWorkspace: async (noteId) => (await api.get(`/google/notes/${noteId}/task`)).data,
  reviewTaskText: async (noteId, payload) => (await api.post(`/google/notes/${noteId}/task/reviews`, payload)).data,
  reviewTaskState: async (noteId, payload) => (await api.post(`/google/notes/${noteId}/task/state-reviews`, payload)).data,
  applyTaskText: async (id) => (await api.post(`/google/task-reviews/${id}/apply`)).data,
  checkTaskText: async (id) => (await api.post(`/google/task-reviews/${id}/check`)).data,
  closeTaskText: async (id) => (await api.post(`/google/task-reviews/${id}/close`)).data,
  // OAuth and Connection
  getAuthUrl: async (mailboxManagement = false, services) => {
    const response = await api.get('/google/auth/url', { params: {
      mailbox_management: mailboxManagement, ...(services ? { services: services.join(',') } : {}),
    } });
    return response.data;
  },

  handleCallback: async (code, state) => {
    const response = await api.post('/google/auth/callback', { code, state });
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/google/status');
    return response.data;
  },

  disconnect: async () => {
    const response = await api.delete('/google/disconnect');
    return response.data;
  },

  // Calendar Operations
  getCalendars: async () => {
    const response = await api.get('/google/calendars');
    return response.data;
  },

  syncCalendar: async (calendarId = 'primary') => {
    const response = await api.post('/google/sync/calendar', null, {
      params: { calendar_id: calendarId }
    });
    return response.data;
  },

  // Notes/Keep Operations
  syncNotes: async () => {
    const response = await api.post('/google/sync/notes');
    return response.data;
  },

  // Full Sync
  fullSync: async () => {
    const response = await api.post('/google/sync/full');
    return response.data;
  },

  // Bidirectional Sync
  bidirectionalSync: async (conflictResolution = 'prefer_latest', options = {}) => {
    const response = await api.post('/google/sync/bidirectional', null, {
      params: { conflict_resolution: conflictResolution, ...options }
    });
    return response.data;
  },

  // Sync Status and Conflicts
  getSyncStatus: async () => {
    const response = await api.get('/google/sync/status');
    return response.data;
  },

  getSyncConflicts: async () => {
    const response = await api.get('/google/sync/conflicts');
    return response.data;
  },

  resolveSyncConflict: async (itemType, itemId, resolution, expectedRevision) => {
    const response = await api.post(`/google/sync/resolve-conflict/${itemType}/${itemId}`, null, {
      params: { resolution, expected_revision: expectedRevision }
    });
    return response.data;
  },

  // Export Operations
  exportEvents: async (calendarId = 'primary', limit = 50) => {
    const response = await api.post('/google/export/events', null, {
      params: { calendar_id: calendarId, limit }
    });
    return response.data;
  },
};

// Enhanced AI Chat services
export const aiChatService = {
  enqueue: async payload => (await api.post('/chat/jobs', payload)).data,
  jobs: async () => (await api.get('/chat/jobs')).data,
  stop: async id => (await api.post(`/chat/jobs/${id}/stop`)).data,
  retry: async id => (await api.post(`/chat/jobs/${id}/retry`)).data,
  steer: async (id, payload) => (await api.post(`/chat/jobs/${id}/steer`, payload)).data,
  captureResearch: async (id, payload) => (await api.post(`/chat/research/${id}/capture`, payload)).data,
  getResearch: async (id) => (await api.get(`/chat/research/${id}`)).data,
  getStatus: async () => {
    const response = await api.get('/chat/status');
    return response.data;
  },

  sendMessage: async (message, context = {}, createEvents = true, options = {}) => {
    const response = await api.post('/chat', { 
      message, 
      context,
      create_events: createEvents,
      ...options,
    });
    return response.data;
  },

  getTurns: async () => (await api.get('/chat/turns')).data,
  requestStatus: async (requestId) => (await api.get(`/chat/requests/${requestId}`)).data,
  decide: async (id, decision) => (await api.post(`/chat/turns/${id}/${decision}`)).data,

  getChatHistory: async (limit = 20) => {
    const response = await api.get('/chat/history', { params: { limit } });
    return response.data;
  },

  getQuickActions: async () => {
    const response = await api.post('/chat/quick-actions');
    return response.data;
  },

  getSuggestions: async () => {
    const response = await api.post('/chat/suggestions');
    return response.data;
  },

  enhanceEvent: async (eventId) => {
    const response = await api.post(`/chat/enhance-event/${eventId}`);
    return response.data;
  },

  analyzeNote: async (noteId) => {
    const response = await api.post(`/chat/analyze-note/${noteId}`);
    return response.data;
  },
};

// Note Processing services
export const noteProcessingService = {
  processNote: async (noteId) => {
    const response = await api.post(`/note-processing/process/${noteId}`);
    return response.data;
  },

  batchProcess: async (limit = 10) => {
    const response = await api.post('/note-processing/batch-process', null, {
      params: { limit }
    });
    return response.data;
  },

  reprocessNote: async (noteId) => {
    const response = await api.post(`/note-processing/reprocess/${noteId}`);
    return response.data;
  },

  getProcessingStatus: async (noteId) => {
    const response = await api.get(`/note-processing/status/${noteId}`);
    return response.data;
  },

  getAnalytics: async () => {
    const response = await api.get('/note-processing/analytics');
    return response.data;
  },

  getPendingCount: async () => {
    const response = await api.get('/note-processing/pending-count');
    return response.data;
  },

  // Google Keep Integration
  syncGoogleKeep: async (syncDirection = 'both') => {
    const response = await api.post('/note-processing/sync-google-keep', null, {
      params: { sync_direction: syncDirection }
    });
    return response.data;
  },

  enhanceWithAI: async (limit = 20) => {
    const response = await api.post('/note-processing/enhance-with-ai', null, {
      params: { limit }
    });
    return response.data;
  },

  getSyncStatus: async () => {
    const response = await api.get('/note-processing/sync-status');
    return response.data;
  },
};

// Smart Scheduling services
export const smartSchedulingService = {
  preparePlan: async (request) => {
    const response = await api.post('/smart-scheduling/plan', request);
    return response.data;
  },
  getInsights: async (window) => {
    const response = await api.get('/smart-scheduling/scheduling-insights', { params: window });
    return response.data;
  },
  findTime: async (request) => {
    const response = await api.post('/smart-scheduling/availability', request);
    return response.data;
  },
};

// Proactive Recommendations services
export const recommendationsService = {
  getRecommendations: async (limit = 10) => {
    const response = await api.get('/recommendations/', { params: { limit } });
    return response.data;
  },

  getRecommendation: async (recommendationId) => {
    const response = await api.get(`/recommendations/${recommendationId}`);
    return response.data;
  },

  acceptRecommendation: async (recommendationId, feedback = null) => {
    const response = await api.post(`/recommendations/${recommendationId}/accept`, { feedback });
    return response.data;
  },

  dismissRecommendation: async (recommendationId, feedback = null) => {
    const response = await api.post(`/recommendations/${recommendationId}/dismiss`, { feedback });
    return response.data;
  },

  generateForEvent: async (eventId) => {
    const response = await api.post(`/recommendations/generate/${eventId}`);
    return response.data;
  },

  getProactiveRecommendations: async () => {
    const response = await api.get('/recommendations/proactive');
    return response.data;
  },

  getContextualRecommendations: async (context) => {
    const response = await api.post('/recommendations/contextual', { context });
    return response.data;
  },

  getSeasonalRecommendations: async () => {
    const response = await api.get('/recommendations/seasonal');
    return response.data;
  },
};

// Preference Learning services
export const preferenceLearningService = {
  saveProfile: async (data) => (await api.put('/preferences/profile', data)).data,
  analyzePreferences: async () => {
    const response = await api.post('/preferences/analyze');
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/preferences/profile');
    return response.data;
  },

  getPersonalizedSuggestions: async (context = {}) => {
    const response = await api.post('/preferences/personalized-suggestions', { context });
    return response.data;
  },

  getAdaptiveResponse: async (message, context = {}) => {
    const response = await api.post('/preferences/adaptive-response', { context }, { params: { message } });
    return response.data;
  },

  getLearningProgress: async () => {
    const response = await api.get('/preferences/learning-progress');
    return response.data;
  },

  provideFeedback: async (feedbackType, itemId, feedback, details = '') => {
    const response = await api.post('/preferences/feedback', {
      feedback_type: feedbackType,
      item_id: itemId,
      feedback,
      details
    });
    return response.data;
  },

  resetPreferences: async (confirm = false) => {
    const response = await api.delete('/preferences/reset', { params: { confirm } });
    return response.data;
  },
};

// MCP Status services
export const mcpService = {
  getStatus: async () => {
    const response = await api.get('/mcp/status');
    return response.data;
  },

  getCapabilities: async () => {
    const response = await api.get('/mcp/capabilities');
    return response.data;
  },

  refreshSavedStatus: async () => {
    const response = await api.get('/assistant/connection-status');
    return response.data;
  },
};

export const connectionStatusService = {
  getStatus: async () => (await api.get('/assistant/connection-status')).data,
  checkAI: async () => (await api.get('/chat/status')).data,
};

export default api;

export const calendarManagementService = {
  get: async (id, beforeId) => (await api.get(`/calendar-management/events/${id}`, {params: beforeId ? {before_id: beforeId} : {}})).data,
  refresh: async (id) => (await api.post(`/calendar-management/events/${id}/refresh`)).data,
  refreshSeries: async (id) => (await api.post(`/calendar-management/events/${id}/series/refresh`)).data,
  prepareSeries: async (id, data) => (await api.post(`/calendar-management/events/${id}/series/changes`, data)).data,
  prepareSeriesCancel: async (id, data) => (await api.post(`/calendar-management/events/${id}/series/cancellation`, data)).data,
  prepareSeriesSchedule: async (id, data) => (await api.post(`/calendar-management/events/${id}/series/schedule`, data)).data,
  prepareFollowingCancel: async (id, data) => (await api.post(`/calendar-management/events/${id}/series/following/cancellation`, data)).data,
  prepareFollowing: async (id, data) => (await api.post(`/calendar-management/events/${id}/series/following`, data)).data,
  resume: async (id) => (await api.post(`/calendar-management/changes/${id}/resume`)).data,
  prepare: async (id, data) => (await api.post(`/calendar-management/events/${id}/changes`, data)).data,
  prepareCancel: async (id, data) => (await api.post(`/calendar-management/events/${id}/cancellation`, data)).data,
  prepareResponse: async (id, data) => (await api.post(`/calendar-management/events/${id}/response`, data)).data,
  apply: async (id) => (await api.post(`/calendar-management/changes/${id}/apply`)).data,
  discard: async (id) => (await api.post(`/calendar-management/changes/${id}/discard`)).data,
  reconcile: async (id) => (await api.post(`/calendar-management/changes/${id}/reconcile`)).data,
};

export const trackerService = {
  sources: async () => (await api.get('/trackers')).data,
  get: async (id, params) => (await api.get(`/trackers/${id}`, { params })).data,
  history: async (id, beforeId) => (await api.get(`/trackers/${id}/history`, { params: beforeId ? { before_id: beforeId } : {} })).data,
  policy: async (id, data) => (await api.put(`/trackers/${id}/policy`, data)).data,
  classification: async (id, data) => (await api.put(`/trackers/${id}/classification`, data)).data,
  prepare: async (id, data) => (await api.post(`/trackers/${id}/updates`, data)).data,
  apply: async (id) => (await api.post(`/trackers/updates/${id}/apply`)).data,
  reconcile: async (id) => (await api.post(`/trackers/updates/${id}/reconcile`)).data,
  cancel: async (id) => (await api.post(`/trackers/updates/${id}/cancel`)).data,
  followup: async (id, rowId, data) => (await api.post(`/trackers/${id}/rows/${rowId}/follow-up`, data)).data,
};

export const assistantService = {
  briefing: async () => (await api.get('/assistant/briefing')).data,
  action: async (id) => (await api.get(`/assistant/actions/${id}`)).data,
  connections: async () => (await api.get('/assistant/connections')).data,
  preferences: async (data) => (await api.put('/assistant/preferences', data)).data,
  create: async (data) => (await api.post('/assistant/responsibilities', data)).data,
  edit: async (id, data) => (await api.put(`/assistant/responsibilities/${id}`, data)).data,
  state: async (id, data) => (await api.post(`/assistant/responsibilities/${id}/state`, data)).data,
  extract: async (content) => (await api.post('/assistant/extract', { content })).data,
  connect: async (data) => (await api.post('/assistant/connections', data)).data,
  disconnect: async (provider) => (await api.delete(`/assistant/connections/${provider}`)).data,
  addSource: async (data) => (await api.post('/assistant/sources', data)).data,
  mailAnalysis: async (id, enabled) => (await api.post(`/assistant/sources/${id}/mail-analysis`, {enabled})).data,
  sync: async (id) => (await api.post(`/assistant/sources/${id}/sync`)).data,
  removeSource: async (id) => (await api.delete(`/assistant/sources/${id}`)).data,
  draft: async (data) => (await api.post('/assistant/actions', data)).data,
  execute: async (id) => (await api.post(`/assistant/actions/${id}/execute`)).data,
  cancel: async (id) => (await api.delete(`/assistant/actions/${id}`)).data,
  research: async (query, location) => (await api.get('/assistant/research', { params: { query, location } })).data,
};

export const agentService = {
  brief: async id => (await api.get(`/agent/briefs/${id}`)).data,
  overview: async () => (await api.get('/agent')).data,
  check: async () => (await api.post('/agent/check')).data,
  decide: async (id, data) => (await api.post(`/agent/proposals/${id}`, data)).data,
  recordProposals: async (params) => (await api.get('/agent/record-proposals', { params })).data,
  decideRecord: async (id, action) => (await api.post(`/agent/record-proposals/${id}`, { action })).data,
  recheckRecord: async (id) => (await api.post(`/agent/record-proposals/${id}/recheck`)).data,
  createWatch: async (data) => (await api.post('/agent/watches', data)).data,
  editWatch: async (id, data) => (await api.put(`/agent/watches/${id}`, data)).data,
  removeWatch: async (id) => (await api.delete(`/agent/watches/${id}`)).data,
  runWatch: async (id) => (await api.post(`/agent/watches/${id}/run`)).data,
  searchPodcasts: async (query, country) => (await api.get('/agent/podcasts/search', {params:{q:query,country}})).data,
  listened: async (id, payload) => (await api.post(`/agent/briefs/${id}/listening`, payload)).data,
  feedback: async (id, value) => (await api.post(`/agent/briefs/${id}/feedback`, { value })).data,
};

export const peopleService = {
  overview: async () => (await api.get('/people')).data,
  create: async (data) => (await api.post('/people', data)).data,
  edit: async (id, data) => (await api.put(`/people/${id}`, data)).data,
  remove: async (id) => (await api.delete(`/people/${id}`)).data,
  contacts: async (id, cursor) => (await api.get(`/people/${id}/contacts`, { params: cursor ? { before_id: cursor } : {} })).data,
  logContact: async (id, data) => (await api.post(`/people/${id}/contacts`, data)).data,
  followUp: async (id, done) => (await api.post(`/people/contacts/${id}/follow-up`, { done })).data,
  removeContact: async (id) => (await api.delete(`/people/contacts/${id}`)).data,
  createOccasion: async (data) => (await api.post('/people/occasions', data)).data,
  editOccasion: async (id, data) => (await api.put(`/people/occasions/${id}`, data)).data,
  removeOccasion: async (id) => (await api.delete(`/people/occasions/${id}`)).data,
  prepare: async (id, data) => (await api.put(`/people/occasions/${id}/preparation`, data)).data,
  giftResearch: async (id, data) => (await api.post(`/people/occasions/${id}/gift-research`, data)).data,
};

export const financeService = {
  overview: async (period) =>
    (await api.get("/finance", { params: period ? { period } : {} })).data,
  createAccount: async (data) =>
    (await api.post("/finance/accounts", data)).data,
  editAccount: async (id, data) =>
    (await api.put(`/finance/accounts/${id}`, data)).data,
  transactions: async (accountId, cursor) =>
    (
      await api.get("/finance/transactions", {
        params: {
          ...(accountId ? { account_id: accountId } : {}),
          ...(cursor ? { before_id: cursor } : {}),
        },
      })
    ).data,
  record: async (data) => (await api.post("/finance/transactions", data)).data,
  reverse: async (id, data) =>
    (await api.post(`/finance/transactions/${id}/reverse`, data)).data,
  reconcile: async (data) =>
    (await api.post("/finance/reconciliations", data)).data,
  adjust: async (id, data) =>
    (await api.post(`/finance/reconciliations/${id}/adjust`, data)).data,
  decide: async (id, data) =>
    (await api.post(`/finance/proposals/${id}`, data)).data,
  draftReport: async (period, sourceId) =>
    (await api.post("/finance/monthly/draft", { period, source_id: sourceId }))
      .data,
};

export const vehicleService = {
  overview: async () => (await api.get('/vehicles')).data,
  create: async (data) => (await api.post('/vehicles', data)).data,
  edit: async (id, data) => (await api.put(`/vehicles/${id}`, data)).data,
  createPlan: async (data) => (await api.post('/vehicles/plans', data)).data,
  editPlan: async (id, data) => (await api.put(`/vehicles/plans/${id}`, data)).data,
  recordReading: async (data) => (await api.post('/vehicles/readings', data)).data,
  recordService: async (data) => (await api.post('/vehicles/services', data)).data,
  history: async (id, kind, cursor) => (await api.get(`/vehicles/${id}/history`, { params: { kind, ...(cursor ? { before_id: cursor } : {}) } })).data,
  void: async (kind, id, reason) => (await api.post(`/vehicles/${kind}/${id}/void`, { reason })).data,
};

export const routineService = {
  research: async (id) => (await api.get(`/routines/${id}/research`)).data,
  saveResearch: async (id, data) => (await api.put(`/routines/${id}/research`, data)).data,
  overview: async () => (await api.get('/routines')).data,
  create: async (data) => (await api.post('/routines', data)).data,
  edit: async (id, data) => (await api.put(`/routines/${id}`, data)).data,
  occurrences: async (id, includeClosed, offset = 0) => (await api.get(`/routines/${id}/occurrences`, { params: { include_closed: includeClosed, offset } })).data,
  decide: async (id, data) => (await api.post(`/routines/occurrences/${id}/decision`, data)).data,
  reschedule: async (id, data) => (await api.post(`/routines/occurrences/${id}/reschedule`, data)).data,
  log: async (data) => (await api.post('/routines/activities', data)).data,
  history: async (id, cursor) => (await api.get(`/routines/${id}/activities`, { params: cursor ? { before_id: cursor } : {} })).data,
  void: async (id, reason) => (await api.post(`/routines/activities/${id}/void`, { reason })).data,
};

export const mailService = {
  work: async id => (await api.get(`/mail/threads/${id}/work`)).data,
  workOptions: async (id, params) => (await api.get(`/mail/threads/${id}/work/options`, {params})).data,
  linkWork: async (id, values) => (await api.post(`/mail/threads/${id}/work`, values)).data,
  labels: async () => (await api.get('/mail/labels')).data,
  attachment: async (id, values) => {
    try {
      return (await api.post(`/mail/threads/${id}/attachments/download`, values, {responseType: 'blob', timeout: 90000})).data;
    } catch (error) {
      // Axios also wraps JSON error responses in a Blob for binary requests.
      const body = error.response?.data;
      if (body instanceof Blob && body.size < 16384) {
        try { error.response.data = JSON.parse(await body.text()); } catch (_) { /* Use the generic download error. */ }
      }
      throw error;
    }
  },
  reviewLabels: async (id, values) => (await api.post(`/mail/threads/${id}/labels/review`, values)).data,
  previewAttachment: async (id, values) => (await api.post(`/mail/threads/${id}/attachments/preview`, values)).data,
  reviewAttachment: async (id, values) => (await api.post(`/mail/threads/${id}/attachments/review`, values)).data,
  applyLabels: async id => (await api.post(`/mail/labels/${id}/apply`)).data,
  reconcileLabels: async id => (await api.post(`/mail/labels/${id}/reconcile`)).data,
  discardLabels: async id => (await api.post(`/mail/labels/${id}/discard`)).data,
  browse: async values => (await api.post('/mail/browse', values)).data,
  openMailbox: async values => (await api.post('/mail/open', values)).data,
  analyse: async id => (await api.post(`/mail/threads/${id}/analyse`)).data,
  list: async offset => (await api.get('/mail', {params:{offset}})).data,
  open: async (source, thread) => (await api.post(`/mail/sources/${source}/threads/${encodeURIComponent(thread)}/open`)).data,
  get: async id => (await api.get(`/mail/threads/${id}`)).data,
  refresh: async id => (await api.post(`/mail/threads/${id}/refresh`)).data,
  reply: async (id, values) => (await api.post(`/mail/threads/${id}/reply`, values)).data,
  watch: async (id, values) => (await api.post(`/mail/threads/${id}/watch`, values)).data,
  reconcile: async id => (await api.post(`/mail/actions/${id}/reconcile`)).data,
};

export const plansService = {
  list: async offset => (await api.get('/plans', {params:{offset}})).data,
  get: async id => (await api.get(`/plans/${id}`)).data,
  create: async values => (await api.post('/plans', values)).data,
  update: async (id, values) => (await api.put(`/plans/${id}`, values)).data,
  history: async (id, offset=0) => (await api.get(`/plans/${id}/history`, {params:{offset}})).data,
  options: async (resource, text='') => (await api.get('/plans/options', {params:{resource,text}})).data,
};

export const mapsService = {
  status: async () => (await api.get('/maps/status')).data,
  search: async payload => (await api.post('/maps/search', payload)).data,
  place: async payload => (await api.post('/maps/place', payload)).data,
  route: async (id, payload) => (await api.post(`/maps/plans/${id}/route`, payload)).data,
};

export const notificationService = {
  gmailSender: async () => (await api.get('/notification-delivery/gmail-sender')).data,
  list: async offset => (await api.get('/notification-delivery', {params:{offset}})).data,
  inbox: async (status, offset) => (await api.get('/notification-delivery/inbox', {params:{status,offset}})).data,
  read: async id => (await api.post(`/assistant/notifications/${id}/read`)).data,
  decide: async (id, values) => (await api.post(`/notification-delivery/${id}/decision`, values)).data,
  legacy: async (id, values) => (await api.post(`/notification-delivery/legacy/${id}/decision`, values)).data,
};

export const documentService = {
  notionNewRow: async source => (await api.get(`/notion-properties/sources/${source}/new-row`)).data,
  notionCreateDraft: async (source, payload) => (await api.post(`/notion-properties/sources/${source}/creations`, payload)).data,
  notionCreations: async (source, offset=0) => (await api.get(`/notion-properties/sources/${source}/creations`, {params: {offset}})).data,
  notionCreationAction: async (id, action, payload={}) => (await api.post(`/notion-properties/creations/${id}/${action}`, payload)).data,
  notionRow: async (source, page) => (await api.get(`/notion-properties/sources/${source}/rows/${page}`)).data,
  notionPeople: async (cursor) => (await api.get('/notion-properties/people', {params: {cursor}})).data,
  notionDraft: async (source, page, payload) => (await api.post(`/notion-properties/sources/${source}/rows/${page}/changes`, payload)).data,
  notionChanges: async (source, offset=0) => (await api.get(`/notion-properties/sources/${source}/changes`, {params: {offset}})).data,
  notionChangeAction: async (id, action, payload) => (await api.post(`/notion-properties/changes/${id}/${action}`, payload)).data,
  receipt: async (key) => (await api.get(`/documents/receipts/${key}`)).data,
  reviewAction: async (id) => (await api.get(`/documents/actions/${id}/review`)).data,
  bindAction: async (id,payload) => (await api.post(`/documents/actions/${id}/bind`,payload)).data,
  reconcileAction: async (id) => (await api.post(`/documents/actions/${id}/reconcile`)).data,
  browse: async (params) => (await api.get('/documents/browse', {params})).data,
  preview: async (payload) => (await api.post('/documents/preview', payload)).data,
  monitor: async (payload) => (await api.post('/documents/monitor', payload)).data,
  list: async (offset=0) => (await api.get('/documents', {params:{offset}})).data,
  get: async (id) => (await api.get(`/documents/${id}`)).data,
  analyse: async (id,payload) => (await api.post(`/documents/${id}/analyse`,payload)).data,
  capture: async (id,payload) => (await api.post(`/documents/${id}/capture`,payload)).data,
  draft: async (id,payload) => (await api.post(`/documents/${id}/draft`,payload)).data,
};


export const socialService = {
  search: async payload => (await api.post('/social/x/search', payload)).data,
  monitor: async payload => (await api.post('/social/x/monitor', payload)).data,
  list: async () => (await api.get('/social/x')).data,
  pause: async (id, paused) => (await api.post(`/social/x/${id}/pause`, {paused})).data,
  analyse: async (id, payload) => (await api.post(`/social/x/${id}/analyse`, payload)).data,
};

export const workspaceService = {
  overview: async () => (await api.get('/workspace/overview')).data,
  search: async q => (await api.get('/workspace/search', {params:{q}})).data,
  preferences: async () => (await api.get('/workspace/preferences')).data,
  savePreferences: async values => (await api.put('/workspace/preferences', values)).data,
};
